import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

const primaryCtaDef: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Contact Our Leasing Team' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

/**
 * Preferred Employer page closing CTA: centred heading, a short blurb, and a
 * primary CTA. Sits directly above the site footer to guide qualified visitors
 * into a leasing conversation. Structurally mirrors ScheduleTourIntro /
 * VirtualTourIntro so the button treatment is consistent across the site.
 */
export function PreferredEmployerCTA() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const primaryCta = useSlotGroup('preferredemployercta.primary_cta', primaryCtaDef);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] pt-[40px] pb-[60px] md:pt-12 md:pb-[80px]"
    >
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path="preferredemployercta.heading"
          label="Heading"
          defaultValue='<p>Ready To Enjoy Your Employer Perks?</p>'
          as="h2"
          className="text-balance text-[clamp(2rem,8.8vw,3.5rem)] sm:text-[clamp(1rem,4.4vw,2.5rem)] uppercase tracking-wide leading-[1.1] mb-6"
          style={{ color: '#000', fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
        />

        <div className="max-w-3xl mx-auto md:px-10 lg:px-0">
          <RichTextContent
            path="preferredemployercta.body"
            label="Body Text"
            defaultValue='<p>Not sure if your company is on our preferred list? Reach out to our leasing team and we&rsquo;ll confirm eligibility, walk you through your benefits, and help you find the floor plan that fits your life at <span data-type="field-token" data-field="property_name"></span>.</p>'
            as="div"
            className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
          />
        </div>

        <div className="mt-8 flex items-center justify-center">
          <EditableSlotGroup groupPath="preferredemployercta.primary_cta" definition={primaryCtaDef}>
            <a
              href={primaryCta.url || '/contact-us'}
              target={primaryCta._target || '_self'}
              rel={primaryCta._target === '_blank' ? 'noopener noreferrer' : undefined}
              className="pec-cta pec-cta--solid inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
            >
              {primaryCta.label || 'Contact Our Leasing Team'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Solid primary pill — matches VirtualTourIntro / site-wide CTA treatment. */
        .pec-cta--solid {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
        }
        .pec-cta--solid:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PreferredEmployerCTA',
  displayName: 'Preferred Employer CTA',
  tokens: {
    'preferredemployercta.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Ready To Enjoy Your Employer Perks?</p>',
    },
    'preferredemployercta.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue:
        '<p>Not sure if your company is on our preferred list? Reach out to our leasing team and we&rsquo;ll confirm eligibility, walk you through your benefits, and help you find the floor plan that fits your life at <span data-type="field-token" data-field="property_name"></span>.</p>',
    },
  },
  slotGroups: {
    'preferredemployercta.primary_cta': primaryCtaDef,
  },
};

(PreferredEmployerCTA as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PreferredEmployerCTA;
